import {Button, Container, Group, NumberInput, Select, Stack, Stepper, Text, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CustomerSelector} from '../components/CustomerSelector';
import {PaymentMethodSelector} from '../components/PaymentMethodSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult, extractErrorMessage} from '../types';
import {add as createPaymentIntent} from '../../payment-intents/services/PaymentIntentsService';
import {notifyError} from '../../../components/ServiceNotifications';

const currencies = ['usd', 'eur', 'gbp', 'cad', 'aud'].map(c => ({value: c, label: c.toUpperCase()}));

export const PayWithSavedMethodPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
    const [amount, setAmount] = useState<number>(1000);
    const [currency, setCurrency] = useState<string>('usd');
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const handleCharge = async () => {
        if (!customerId || !paymentMethodId || !amount) return;
        setProcessing(true);
        try {
            const pi = await createPaymentIntent({
                amount,
                currency,
                customerId,
                paymentMethod: paymentMethodId,
                description: 'Payment Flow Tester - pay with saved method',
            });
            console.log('Payment intent created with saved method:', pi);
            setResult({
                success: true,
                intentId: (pi as { id: string })?.id,
                paymentMethodId,
                status: (pi as { status: string })?.status,
            });
            setActive(1);
        } catch (e) {
            notifyError(e);
            setResult({success: false, error: extractErrorMessage(e)});
            setActive(1);
        } finally {
            setProcessing(false);
        }
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setPaymentMethodId(null);
        setAmount(1000);
        setCurrency('usd');
        setResult(null);
    };

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>Pay with Saved Payment Method</Title>
                    <Text c="dimmed" size="sm">
                        Charge a previously saved payment method without collecting card details again.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Select Details"/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={setCustomerId}/>
                        <PaymentMethodSelector value={paymentMethodId} onChange={setPaymentMethodId} customerId={customerId}/>
                        <NumberInput
                            label="Amount (in cents)"
                            placeholder="e.g. 1000 = $10.00"
                            value={amount}
                            onChange={v => setAmount(Number(v))}
                            min={50}
                        />
                        <Select
                            label="Currency"
                            data={currencies}
                            value={currency}
                            onChange={v => setCurrency(v || 'usd')}
                        />
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>Back</Button>
                            <Button
                                onClick={handleCharge}
                                disabled={!customerId || !paymentMethodId || !amount}
                                loading={processing}
                            >
                                Charge Payment Method
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
